import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { messageToRows } from './moment_stream_helpers';
import {
  startMomentRun,
  pollMomentThread,
  listMoments,
  archiveMoment,
  setMomentProductState,
  setMomentFeatured,
  stopMomentRun,
  retryMomentRun
} from '../../api/moments';

const ACTIVE_POLL_MS = 1500;
const IDLE_POLL_MS = 6000;

/*
  The bench's shared state (the react.mdc self-contained feature pattern):
  routing, polling, run state, and the editor actions live here; the views
  (MomentsGallery / MomentsBuilder / MomentShopPane) consume useMoments()
  instead of threading a dozen props through the container.
*/
const MomentsContext = createContext(null);

export const useMoments = () => {
  const context = useContext(MomentsContext);
  if (!context) throw new Error('useMoments must be used within a MomentsProvider');
  return context;
};

export const MomentsProvider = ({ children }) => {
  const history = useHistory();
  // URL owns the view: /moments = gallery, /moments/new = fresh builder,
  // /moments/<id> or /moments/<slug> = that moment's builder.
  const { momentKey } = useParams();
  const view = momentKey ? 'builder' : 'gallery';
  const [gallery, setGallery] = useState(null); // null = loading
  const [galleryError, setGalleryError] = useState(null);
  const [threadRef, setThreadRef] = useState(null);
  const [messages, setMessages] = useState([]);
  const [moment, setMoment] = useState(null);
  const [runStatus, setRunStatus] = useState('idle');
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  // True from the stop request until the run.cancelled webhook lands (a later
  // poll reports the run gone) — keeps the Stop button from double-firing.
  const [stopping, setStopping] = useState(false);
  // Product ids with a curation round-trip in flight — only the acted-on card
  // disables, so an editor can remove several products back to back.
  const [busyProductIds, setBusyProductIds] = useState(() => new Set());
  // True from opening an existing moment until its first poll lands — keeps the
  // "start from a moment" empty state from flashing while the transcript loads.
  const [threadLoading, setThreadLoading] = useState(false);

  const pollTimerRef = useRef(null);
  const threadRefRef = useRef(threadRef);
  const composerRef = useRef(null);
  // schedulePoll and pollOnce depend on each other; the timeout closes over
  // this ref (kept current below) instead of a possibly-stale pollOnce.
  const pollOnceRef = useRef(null);
  // The last momentUpdatedAt the poll returned — echoed on the next poll so
  // an unchanged shop skips the server-side slate hydration.
  const momentUpdatedAtRef = useRef(null);
  // False after unmount. The unmount cleanup can only clear the *pending*
  // timer — a poll already in flight resolves afterward and would otherwise
  // re-arm the timer chain forever. Both re-arm paths check this flag.
  const aliveRef = useRef(true);

  threadRefRef.current = threadRef;
  const isRunning = runStatus === 'running' || sending;
  const showMoment = !!(moment && moment.products && moment.products.length);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const schedulePoll = useCallback(
    delayMs => {
      stopPolling();
      if (!aliveRef.current) return;
      pollTimerRef.current = setTimeout(() => pollOnceRef.current && pollOnceRef.current(), delayMs);
    },
    [stopPolling]
  );

  const pollOnce = useCallback(async () => {
    const ref = threadRefRef.current;
    if (!ref || !aliveRef.current) return;
    try {
      // Echo the last moment stamp we rendered: an unchanged shop skips the
      // server's slate hydration and returns products: null (= keep ours).
      const data = await pollMomentThread(ref, momentUpdatedAtRef.current);
      if (!aliveRef.current) return; // unmounted mid-flight — don't write or re-arm
      if (threadRefRef.current !== ref) return; // thread switched mid-flight
      // The poll returns the full persisted transcript; map it wholesale.
      if (Array.isArray(data.messages)) setMessages(data.messages.flatMap(messageToRows));
      if (data.moment) {
        setMoment(previous => (data.moment.products === null && previous ? { ...data.moment, products: previous.products } : data.moment));
        momentUpdatedAtRef.current = data.momentUpdatedAt || null;
      }
      setRunStatus(data.runStatus || 'idle');
      if (data.runStatus !== 'running') setStopping(false);
      setThreadLoading(false);
      schedulePoll(data.runStatus === 'running' ? ACTIVE_POLL_MS : IDLE_POLL_MS);
    } catch {
      if (!aliveRef.current) return;
      if (threadRefRef.current === ref) setThreadLoading(false);
      schedulePoll(IDLE_POLL_MS);
    }
  }, [schedulePoll]);

  pollOnceRef.current = pollOnce;

  const refreshGallery = useCallback(async () => {
    setGalleryError(null);
    try {
      const data = await listMoments();
      setGallery(Array.isArray(data.moments) ? data.moments : []);
    } catch (err) {
      // Distinguish a failed load from an empty system — the empty state's
      // "build the first one" CTA is the wrong message for an outage. And a
      // 404 means the server registered no moments routes at all
      // (MOMENT_SHOPS_ENABLED off) — config, not an outage.
      setGallery(null);
      setGalleryError(
        err?.status === 404
          ? 'Moments are not enabled on this server (MOMENT_SHOPS_ENABLED).'
          : "Couldn't load Moments. The server may be unreachable."
      );
    }
  }, []);

  const resetBuilderState = useCallback(() => {
    stopPolling();
    setThreadRef(null);
    threadRefRef.current = null;
    momentUpdatedAtRef.current = null;
    setMessages([]);
    setMoment(null);
    setRunStatus('idle');
    setStopping(false);
    setThreadLoading(false);
    setInput('');
  }, [stopPolling]);

  // The URL param drives what's open. Numeric keys map straight to the
  // thread (Moment:<id>); a published moment's slug resolves through the
  // gallery listing. 'new' is the empty builder awaiting a first brief.
  useEffect(() => {
    let cancelled = false;
    const openFromKey = async () => {
      if (!momentKey) {
        resetBuilderState();
        refreshGallery();
        return;
      }
      // Creation lives on the shopper surface (threads → brief → build);
      // the /moments/new deep link lands on the observation table.
      if (momentKey === 'new') {
        history.replace('/moments');
        return;
      }
      let ref = /^\d+$/.test(momentKey) ? `Moment:${momentKey}` : null;
      if (!ref) {
        try {
          const data = await listMoments();
          if (cancelled) return;
          const match = (data.moments || []).find(entry => entry.slug === momentKey);
          ref = match ? match.threadRef : null;
        } catch {
          ref = null;
        }
      }
      if (!ref) {
        history.replace('/moments');
        return;
      }
      if (ref === threadRefRef.current) return;
      resetBuilderState();
      setThreadRef(ref);
      threadRefRef.current = ref;
      setThreadLoading(true);
      pollOnce();
    };
    openFromKey();
    return () => {
      cancelled = true;
    };
  }, [momentKey, history, pollOnce, refreshGallery, resetBuilderState]);

  useEffect(() => {
    aliveRef.current = true; // remount-safe: the inline-route pattern remounts the bench
    return () => {
      aliveRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  // Once a moment is published its slug is the nicer address — upgrade a
  // numeric URL in place (no navigation, no re-open).
  useEffect(() => {
    if (moment && moment.slug && momentKey && momentKey !== moment.slug && String(moment.id) === momentKey) {
      history.replace(`/moments/${moment.slug}`);
    }
  }, [moment, momentKey, history]);

  // Auto-grow the prompt textarea.
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, [input]);

  const send = useCallback(
    async text => {
      const message = (text || '').trim();
      if (!message || sending) return;
      setSending(true);
      try {
        const ack = await startMomentRun({ message, threadRef: threadRefRef.current });
        setInput('');
        if (ack.threadRef && ack.threadRef !== threadRefRef.current) {
          setMessages([]);
          setThreadRef(ack.threadRef);
          threadRefRef.current = ack.threadRef;
          // Address the fresh moment (Moment:<id> → /moments/<id>) without a re-open.
          const newId = String(ack.threadRef).replace(/^Moment:/, '');
          if (newId) history.replace(`/moments/${newId}`);
        }
        setRunStatus('running');
        window.dispatchEvent(new Event('autopilot:scroll-to-latest'));
        schedulePoll(400);
      } catch (err) {
        window.ALERT.error(typeof err === 'string' ? err : 'Could not start the build.');
      } finally {
        setSending(false);
      }
    },
    [sending, schedulePoll, history]
  );

  const openMoment = useCallback(
    entry => {
      history.push(`/moments/${entry.slug || entry.id}`);
    },
    [history]
  );

  const backToGallery = useCallback(() => {
    // Keep the stale gallery on screen; the momentKey effect refreshes it in
    // place — no loading flash on the way back.
    history.push('/moments');
  }, [history]);

  const runEditorAction = useCallback(
    async (action, successMessage) => {
      if (actionBusy) return;
      setActionBusy(true);
      try {
        await action();
        await pollOnce();
        // Close the feedback loop on weighty actions (archive takes a live
        // shop offline) instead of leaving the operator to watch for the chip.
        if (successMessage) window.ALERT.success(successMessage);
      } catch (err) {
        window.ALERT.error(typeof err === 'string' ? err : 'The action failed.');
      } finally {
        setActionBusy(false);
      }
    },
    [actionBusy, pollOnce]
  );

  const runProductAction = useCallback(
    async (productId, action) => {
      if (busyProductIds.has(productId)) return;
      setBusyProductIds(previous => new Set(previous).add(productId));
      try {
        await action();
        await pollOnce();
      } catch (err) {
        window.ALERT.error(typeof err === 'string' ? err : 'The action failed.');
      } finally {
        setBusyProductIds(previous => {
          const next = new Set(previous);
          next.delete(productId);
          return next;
        });
      }
    },
    [busyProductIds, pollOnce]
  );

  // Purposed editor actions, bound to the open moment — views never compose
  // API calls themselves.
  const archive = useCallback(() => runEditorAction(() => archiveMoment(moment.id), 'Moment archived.'), [runEditorAction, moment]);
  const setFeatured = useCallback(
    featured =>
      runEditorAction(
        () => setMomentFeatured(moment.id, featured),
        featured ? 'Featured — it is on the shopper discover page.' : 'Removed from the discover page.'
      ),
    [runEditorAction, moment]
  );

  // The gallery's per-row featured toggle — same action, bound by row id
  // instead of the open moment. One row busy at a time keeps it simple.
  const [featuringId, setFeaturingId] = useState(null);
  const setFeaturedFor = useCallback(
    async (momentId, featured) => {
      if (featuringId) return;
      setFeaturingId(momentId);
      try {
        await setMomentFeatured(momentId, featured);
        await refreshGallery();
        window.ALERT.success(featured ? 'Featured — it is on the shopper discover page.' : 'Removed from the discover page.');
      } catch (err) {
        window.ALERT.error(typeof err === 'string' ? err : 'Could not update featured.');
      } finally {
        setFeaturingId(null);
      }
    },
    [featuringId, refreshGallery]
  );
  const setProductState = useCallback((productId, state) => runProductAction(productId, () => setMomentProductState(moment.id, productId, state)), [
    runProductAction,
    moment
  ]);

  // Stop asks shopkeep to cancel the active run; the authoritative outcome is
  // the run.cancelled webhook, so `stopping` stays on until a poll reports the
  // run gone. A healed response means the run was already dead — poll now.
  const stop = useCallback(async () => {
    const momentId = (moment && moment.id) || parseInt(String(threadRefRef.current || '').replace(/^Moment:/, ''), 10);
    if (!momentId || stopping) return;
    setStopping(true);
    try {
      const ack = await stopMomentRun(momentId);
      if (ack && ack.healed) {
        setStopping(false);
        await pollOnce();
      }
    } catch (err) {
      setStopping(false);
      window.ALERT.error(typeof err === 'string' ? err : 'Could not stop the build.');
    }
  }, [moment, stopping, pollOnce]);

  // Retry re-runs the last build message server-side (no retyping); the poll
  // right after picks up the running state and tightens the cadence.
  const retry = useCallback(
    () =>
      runEditorAction(async () => {
        await retryMomentRun(moment.id);
        setRunStatus('running');
        window.dispatchEvent(new Event('autopilot:scroll-to-latest'));
      }),
    [runEditorAction, moment]
  );

  const value = {
    // view + gallery
    view,
    gallery,
    galleryError,
    refreshGallery,
    openMoment,
    backToGallery,
    // builder
    threadRef,
    messages,
    moment,
    isRunning,
    sending,
    threadLoading,
    showMoment,
    input,
    setInput,
    composerRef,
    send,
    // editor actions
    actionBusy,
    busyProductIds,
    archive,
    setFeatured,
    setFeaturedFor,
    featuringId,
    setProductState,
    // run controls
    stopping,
    stop,
    retry
  };

  return <MomentsContext.Provider value={value}>{children}</MomentsContext.Provider>;
};
